import classNames from 'classnames';
import css from './ListingOverview.module.css';
import { AvatarSmall } from '../Avatar/Avatar';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NamedLink } from '../index';
import { H4 } from '../../containers/PageBuilder/Primitives/Heading';
import { createSlug } from '../../util/urlHelpers';
import { PrimaryButton } from '../mui/Button';
import { checkoutNamedLinkProps } from '../../util/digitalSubscription';

const ListingAuthorComponent = ({ author, disableProfileLink, rootClassName, authorLine }) => (
  <div className={rootClassName || css.ListingAuthorRoot}>
    <div className={css.ListingAuthor}>
      <AvatarSmall user={author} disableProfileLink={disableProfileLink} />
      <div>{authorLine}</div>
    </div>
  </div>
);

const ListingTitleComponent = ({
  listing,
  location,
  rootClassName,
  showListingLink,
  showContentLibraryPrimaryInOverview,
  contentLibraryPrimaryCta = 'library',
  contentLibraryTransactionId,
}) => {
  const intl = useIntl();
  const listingPageParams = {
    id: listing?.id?.uuid,
    slug: createSlug(listing?.attributes?.title),
  };
  const listingSearch = { search: location.search };
  const libraryLinkProps = contentLibraryTransactionId
    ? {
        name: 'ContentLibraryDetailPage',
        params: { transactionId: contentLibraryTransactionId },
      }
    : { name: 'ContentLibraryPage' };
  const checkoutLinkProps = checkoutNamedLinkProps(listing);

  const libraryButton = (
    <PrimaryButton
      component="span"
      className={css.contentLibraryPrimaryBtn}
      sx={{ width: 'auto' }}
      disabled={contentLibraryPrimaryCta === 'libraryDisabled'}
      data-testid="listing-overview-library-cta"
    >
      <FormattedMessage id="ListingOverview.goToMyLibrary" />
    </PrimaryButton>
  );

  const renewButton = (
    <PrimaryButton
      component="span"
      className={css.contentLibraryPrimaryBtn}
      sx={{ width: 'auto' }}
      data-testid="listing-overview-renew-cta"
    >
      <FormattedMessage id="ListingOverview.renewSubscription" />
    </PrimaryButton>
  );

  const primaryCta =
    showContentLibraryPrimaryInOverview &&
    contentLibraryPrimaryCta === 'renew' &&
    checkoutLinkProps ? (
      <NamedLink {...checkoutLinkProps} className={css.contentLibraryPrimaryWrap}>
        {renewButton}
      </NamedLink>
    ) : showContentLibraryPrimaryInOverview && contentLibraryPrimaryCta === 'libraryDisabled' ? (
      <div className={css.contentLibraryPrimaryWrap}>{libraryButton}</div>
    ) : showContentLibraryPrimaryInOverview ? (
      <NamedLink {...libraryLinkProps} className={css.contentLibraryPrimaryWrap}>
        {libraryButton}
      </NamedLink>
    ) : null;

  return (
    <div className={rootClassName || css.ListingTitleRoot}>
      <div className={css.ListingTitle}>
        <H4 as="h2" className={css.ListingTitleHeading}>
          {listing?.attributes?.title}
        </H4>
        {showListingLink && listing?.attributes?.title ? (
          showContentLibraryPrimaryInOverview ? (
            <div className={css.listingLinksRow}>{primaryCta}</div>
          ) : (
            <NamedLink
              className={css.ListingTitleLink}
              name="ListingPage"
              params={listingPageParams}
              to={listingSearch}
            >
              {intl.formatMessage({ id: 'ListingOverview.viewListingDetails' })}
            </NamedLink>
          )
        ) : null}
      </div>
    </div>
  );
};

export const ListingOverview = ({
  author,
  listing,
  location,
  showListingLink,
  showContentLibraryPrimaryInOverview = false,
  contentLibraryPrimaryCta = 'library',
  contentLibraryTransactionId = null,
  showAuthor = true,
  disableProfileLink = true,
  rootClassName,
}) => {
  const intl = useIntl();
  const rootClasses = classNames(rootClassName || css.ListingOverviewRoot);
  const authorLine =
    author?.attributes?.profile?.displayName != null
      ? intl.formatMessage(
          { id: 'ListingOverview.byAuthor' },
          { name: author.attributes.profile.displayName }
        )
      : null;

  return (
    <div className={rootClasses}>
      {showAuthor && author && authorLine ? (
        <ListingAuthorComponent
          author={author}
          disableProfileLink={disableProfileLink}
          authorLine={authorLine}
        />
      ) : null}
      <ListingTitleComponent
        location={location}
        listing={listing}
        showListingLink={showListingLink}
        showContentLibraryPrimaryInOverview={showContentLibraryPrimaryInOverview}
        contentLibraryPrimaryCta={contentLibraryPrimaryCta}
        contentLibraryTransactionId={contentLibraryTransactionId}
      />
    </div>
  );
};

ListingOverview.defaultProps = {
  showContentLibraryPrimaryInOverview: false,
  contentLibraryPrimaryCta: 'library',
  contentLibraryTransactionId: null,
};
